import { ArrowRight, Broadcast } from "@phosphor-icons/react";
import { isSupportedUrl, detectPlatform } from "@/lib/youtube";
import { useState, useMemo } from "react";
import PlatformBadge from "./PlatformBadge";

export default function UrlForm({ onSubmit, disabled, compact = false }) {
  const [url, setUrl] = useState("");
  const [error, setError] = useState("");

  const detected = useMemo(() => detectPlatform(url), [url]);

  const submit = (e) => {
    e.preventDefault();
    setError("");
    const trimmed = url.trim();
    if (!isSupportedUrl(trimmed)) {
      setError("Paste a YouTube, TikTok, Instagram, X/Twitter, or Facebook video URL.");
      return;
    }
    onSubmit(trimmed);
  };

  const examples = [
    { label: "YouTube · JFK speech", url: "https://www.youtube.com/watch?v=PEC1C4p0k3E" },
  ];

  if (compact) {
    return (
      <form onSubmit={submit} data-testid="url-form-compact" className="w-full">
        <div className="flex items-center gap-2 border-b-2 border-zinc-300 focus-within:border-black transition-colors pb-1.5">
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="Check another video URL…"
            data-testid="url-input-compact"
            disabled={disabled}
            className="flex-1 bg-transparent font-mono-plex text-[13px] normal-case tracking-normal text-zinc-950 placeholder:text-zinc-400 focus:outline-none py-1"
          />
          {detected && <PlatformBadge platform={detected} />}
          <button
            type="submit"
            disabled={disabled}
            data-testid="submit-url-button-compact"
            className="group flex items-center gap-1.5 bg-black text-white px-3.5 py-2 font-mono-plex text-[10px] font-semibold hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {disabled ? (
              <>
                <Broadcast weight="fill" className="w-3.5 h-3.5 animate-pulse" />
                ANALYZING
              </>
            ) : (
              <>
                CHECK
                <ArrowRight weight="bold" className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
              </>
            )}
          </button>
        </div>
        {error && (
          <p data-testid="url-error-compact" className="text-[11px] text-red-700 mt-1.5 font-mono-plex normal-case tracking-normal">
            {error}
          </p>
        )}
      </form>
    );
  }

  return (
    <form onSubmit={submit} data-testid="url-form" className="w-full">
      <div className="flex flex-col md:flex-row md:items-end gap-4 border-b-2 border-zinc-300 focus-within:border-black transition-colors pb-3">
        <div className="flex-1">
          <div className="flex items-center justify-between mb-2">
            <label className="font-mono-plex text-[10px] font-semibold text-zinc-500">
              PASTE A VIDEO URL — WE&apos;LL DO THE REST
            </label>
            {detected && <PlatformBadge platform={detected} />}
          </div>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://youtube.com/... · tiktok.com/... · instagram.com/... · x.com/..."
            data-testid="url-input"
            disabled={disabled}
            className="w-full bg-transparent font-display text-2xl md:text-3xl text-zinc-950 placeholder:text-zinc-400 focus:outline-none py-1"
          />
        </div>
        <button
          type="submit"
          disabled={disabled}
          data-testid="submit-url-button"
          className="group flex items-center gap-2 bg-black text-white px-6 py-3.5 font-mono-plex text-xs font-semibold hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors self-start md:self-end"
        >
          {disabled ? (
            <>
              <Broadcast weight="fill" className="w-4 h-4 animate-pulse" />
              ANALYZING
            </>
          ) : (
            <>
              ANALYZE
              <ArrowRight weight="bold" className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </>
          )}
        </button>
      </div>
      {error && (
        <p data-testid="url-error" className="text-sm text-red-700 mt-3 font-mono-plex normal-case tracking-normal">
          {error}
        </p>
      )}
      <div className="mt-3 flex items-center gap-3 flex-wrap font-mono-plex text-[10px] text-zinc-500">
        <span>WORKS WITH:</span>
        <span className="text-zinc-800">YOUTUBE · TIKTOK · INSTAGRAM · X · FACEBOOK</span>
        <span className="text-amber-700 border border-amber-300 bg-amber-50 px-1.5 py-0.5">
          BEST RESULTS ON YOUTUBE
        </span>
      </div>
      <div className="mt-3 flex flex-wrap gap-3 text-[11px] text-zinc-500 font-mono-plex">
        <span>TRY:</span>
        {examples.map((ex) => (
          <button
            key={ex.url}
            type="button"
            onClick={() => setUrl(ex.url)}
            data-testid={`try-example-${ex.url.slice(-11)}`}
            className="underline decoration-dotted hover:text-zinc-900 normal-case tracking-normal"
          >
            {ex.label}
          </button>
        ))}
      </div>
    </form>
  );
}
